import React, {
  createContext,
  useContext,
  useState,
  useEffect
} from "react";
import axios from "axios";

// Use environment variable with fallback
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://api.saamidoon.com";

console.log("🔧 Environment check:");
console.log("REACT_APP_BACKEND_URL:", process.env.REACT_APP_BACKEND_URL);
console.log("API_BASE_URL:", API_BASE_URL);
console.log("NODE_ENV:", process.env.NODE_ENV);

// Create context
const AuthContext = createContext(null);

// Custom hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};

// Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(
    localStorage.getItem("token")
  );
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  // Init axios + load user
  useEffect(() => {
    console.log("🔧 AuthProvider mounted");
    console.log("API Base URL:", API_BASE_URL);
    
    // Set axios defaults
    axios.defaults.baseURL = API_BASE_URL;
    axios.defaults.headers.common['Accept'] = 'application/json';
    axios.defaults.headers.common['Content-Type'] = 'application/json';
    
    // For debugging, log all requests
    axios.interceptors.request.use(request => {
      console.log('📤 Request:', request.method, request.url, request.data);
      return request;
    });
    
    axios.interceptors.response.use(response => {
      console.log('📥 Response:', response.status, response.config.url);
      return response;
    }, error => {
      console.log('❌ Response Error:', error.response?.status, error.config?.url, error.message);
      return Promise.reject(error);
    });

    if (token) {
      console.log("🔑 Token found in localStorage");
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      loadCurrentUser();
    } else {
      console.log("🔒 No token found");
      setLoading(false);
    }
  }, []);

  // Load logged-in user
  const loadCurrentUser = async () => {
    try {
      console.log("👤 Loading current user from:", `${API_BASE_URL}/api/auth/me`);
      const res = await axios.get("/api/auth/me");

      if (res.data?.success) {
        console.log("✅ User loaded:", res.data.data);
        setUser(res.data.data);
      } else {
        console.log("❌ Failed to load user:", res.data);
        clearAuth();
      }
    } catch (error) {
      console.error("❌ Auth error:", error);
      console.error("Error details:", error.response?.data || error.message);
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  // Login - FIXED VERSION
  const login = async (email, password) => {
    try {
      setAuthError("");
      console.log("🔐 Attempting login to:", `${API_BASE_URL}/api/auth/login`);
      console.log("📧 Email:", email);
      
      // Make POST request
      const res = await axios.post(
        "/api/auth/login",
        { email, password },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      console.log("📨 Login response:", res.data);

      if (!res.data?.success) {
        const errorMsg = res.data?.message || "Login failed";
        console.log("❌ Login failed:", errorMsg);
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const { token: jwtToken, user: loggedUser } = res.data.data;

      console.log("✅ Login successful!");
      console.log("Token:", jwtToken?.substring(0, 20) + "...");
      console.log("User:", loggedUser);

      // Save token
      localStorage.setItem("token", jwtToken);
      setToken(jwtToken);
      setUser(loggedUser);

      // Set axios header
      axios.defaults.headers.common.Authorization = `Bearer ${jwtToken}`;

      return { success: true };
      
    } catch (error) {
      console.error("❌ Login error details:");
      console.error("Error object:", error);
      
      let errorMessage = "Login failed";
      
      // Handle different error types
      if (error.response) {
        // Server responded with error status
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
        console.error("Response headers:", error.response.headers);
        
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        
        // Check for CORS errors
        if (error.response.status === 0) {
          errorMessage = "CORS error: Cannot connect to API. Check CORS configuration.";
        }
      } else if (error.request) {
        // Request was made but no response
        console.error("No response received. Request:", error.request);
        errorMessage = "No response from server. Check if API is running and CORS is configured.";
      } else {
        // Something else
        console.error("Error message:", error.message);
        errorMessage = error.message || "Login failed";
      }
      
      // Check for CORS specifically
      if (errorMessage.includes('CORS') || errorMessage.includes('origin') || errorMessage.includes('Access-Control')) {
        errorMessage = `CORS Error: ${errorMessage}. Make sure https://api.saamidoon.com allows requests from https://admin.saamidoon.com`;
      }
      
      console.log("📢 Error message to user:", errorMessage);
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Register
  const register = async (formData) => {
    try {
      setAuthError("");
      console.log("📝 Attempting registration");
      const res = await axios.post(
        "/api/auth/register",
        formData,
        { 
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          } 
        }
      );

      console.log("📨 Registration response:", res.data);

      if (res.data?.success) {
        return { success: true };
      }

      const errorMsg = res.data?.message || "Registration failed";
      console.log("❌ Registration failed:", errorMsg);
      setAuthError(errorMsg);
      return { success: false, error: errorMsg };
      
    } catch (error) {
      console.error("❌ Registration error:", error);
      const errorMsg = error.response?.data?.message || "Registration failed";
      setAuthError(errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    }
  };

  // Logout
  const logout = () => {
    console.log("🚪 Logging out...");
    clearAuth();
  };

  // Clear auth helper
  const clearAuth = () => {
    console.log("🧹 Clearing auth data...");
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common.Authorization;
    console.log("✅ Auth cleared");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        authError,
        login,
        register,
        logout,
        currentUser: user,
        setAuthError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};